import React, { useState, useEffect } from 'react';
import api from '../services/axiosInstance';
import DataGridList from '../components/grid/DataGridList';

export default function GroupConfiguration() {
    const [servers, setServers] = useState([]);
    const [serverTypes, setServerTypes] = useState([]);
    const [selectedServerId, setSelectedServerId] = useState('');
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const resServers = await api.get('/releases/servers');
                const sorted = resServers.data.sort((a, b) => (a.groupName || '').localeCompare(b.groupName || ''));
                setServers(sorted);

                const resTypes = await api.get('/servertypes');
                setServerTypes(resTypes.data);
            } catch (e) {
                console.error(e);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (!selectedServerId) {
            setConfig(null);
            return;
        }
        const loadConfig = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/serverconfig/${selectedServerId}`);
                setConfig(res.data);
            } catch (e) {
                console.error(e);
                console.error(e);
                setConfig({ serverTypeId: null, generalSettings: {}, schedules: [] });
            } finally {
                setLoading(false);
            }
        };
        loadConfig();
    }, [selectedServerId]);

    const handleServerTypeChange = (e) => {
        const val = e.target.value;
        setConfig(prev => ({
            ...prev,
            serverTypeId: val ? parseInt(val) : null
        }));
    };

    const handleSettingChange = (key, value) => {
        setConfig(prev => ({
            ...prev,
            generalSettings: {
                ...prev.generalSettings,
                [key]: value
            }
        }));
    };

    const handleSave = async () => {
        if (!selectedServerId || !config) return;
        setSaving(true);
        try {
            await api.post(`/serverconfig/${selectedServerId}`, config);
            alert('Configuration saved successfully!');
        } catch (e) {
            console.error(e);
            alert('Failed to save configuration.');
        } finally {
            setSaving(false);
        }
    };

    const scheduleColumns = [
        { dataField: "scheduleName", caption: "Name" },
        { dataField: "scheduleType", caption: "Type" },
        { dataField: "exportType", caption: "Export" },
        { dataField: "intervalMinutes", caption: "Interval (Mins)" },
        { dataField: "time", caption: "Time" },
        { dataField: "daysOfWeek", caption: "Days" },
        { dataField: "active", caption: "Active", dataType: "boolean" },
    ];

    return (
        <div className="portal-stack" style={{ maxWidth: "1200px" }}>
            <header className="dashboard-header">
                <div className="dashboard-title-area">
                    <h1>Group Configuration</h1>
                    <p>Manage general settings and price update schedules per instance.</p>
                </div>
            </header>
            
            <section className="portal-panel" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Select Server:</span>
                    <select 
                        className="toolbar-select" 
                        value={selectedServerId} 
                        onChange={e => setSelectedServerId(e.target.value)}
                        style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--input-bg)', color: 'var(--text-color)' }}
                    >
                        <option value="">-- Select Server --</option>
                        {servers.map(s => (
                            <option key={s.dbServerId || s.serverId} value={s.dbServerId || s.serverId}>
                                {s.groupName} ({s.serverId})
                            </option>
                        ))}
                    </select>
                </div>
            </section>

            {loading && <p>Loading configuration...</p>}

            {!loading && config && (
                <>
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
                        <button 
                            className={`action-btn ${activeTab === 'general' ? 'active-tab' : ''}`}
                            style={{ 
                                padding: '10px 16px', 
                                border: 'none', 
                                background: activeTab === 'general' ? 'var(--input-bg)' : 'transparent',
                                borderBottom: activeTab === 'general' ? '2px solid var(--brand-mid)' : 'none',
                                color: activeTab === 'general' ? 'var(--text-color)' : 'var(--text-muted)',
                                fontWeight: activeTab === 'general' ? 'bold' : 'normal',
                                cursor: 'pointer'
                            }}
                            onClick={() => setActiveTab('general')}
                        >
                            General Settings
                        </button>
                        <button 
                            className={`action-btn ${activeTab === 'schedules' ? 'active-tab' : ''}`}
                            style={{ 
                                padding: '10px 16px', 
                                border: 'none', 
                                background: activeTab === 'schedules' ? 'var(--input-bg)' : 'transparent',
                                borderBottom: activeTab === 'schedules' ? '2px solid var(--brand-mid)' : 'none',
                                color: activeTab === 'schedules' ? 'var(--text-color)' : 'var(--text-muted)',
                                fontWeight: activeTab === 'schedules' ? 'bold' : 'normal',
                                cursor: 'pointer'
                            }}
                            onClick={() => setActiveTab('schedules')}
                        >
                            Price Update Schedules
                        </button>
                    </div>

                    {activeTab === 'general' && (
                        <section className="portal-panel" style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-color)' }}>Release Channel (Server Type)</label>
                                        <select 
                                            className="toolbar-select" 
                                            value={config.serverTypeId ?? ''} 
                                            onChange={handleServerTypeChange}
                                            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--input-bg)', color: 'var(--text-color)' }}
                                        >
                                            <option value="">-- Unassigned (Live/Global) --</option>
                                            {serverTypes.map(st => (
                                                <option key={st.id} value={st.id}>{st.name} ({st.sftpFolder})</option>
                                            ))}
                                        </select>
                                        <p style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                            Assign this server to a specific release channel to receive independent updates.
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-color)' }}>Updater Start Time</label>
                                        <input type="time" className="toolbar-input" step="1" value={config.generalSettings?.Updater_StartTime || '20:00:00'} onChange={e => handleSettingChange('Updater_StartTime', e.target.value)} />
                                    </div>
                                </div>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <label style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-color)', marginBottom: '4px' }}>Database Maintenance</label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                        <input type="checkbox" checked={config.generalSettings?.DBMaintenance_Backup === 'True'} onChange={e => handleSettingChange('DBMaintenance_Backup', e.target.checked ? 'True' : 'False')} />
                                        Create Backup
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                        <input type="checkbox" checked={config.generalSettings?.DBMaintenance_Shrink === 'True'} onChange={e => handleSettingChange('DBMaintenance_Shrink', e.target.checked ? 'True' : 'False')} />
                                        Shrink Database
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                        <input type="checkbox" checked={config.generalSettings?.DBMaintenance_RestartSql === 'True'} onChange={e => handleSettingChange('DBMaintenance_RestartSql', e.target.checked ? 'True' : 'False')} />
                                        Restart SQL Service
                                    </label>
                                </div>
                            </div>
                        </section>
                    )}

                    {activeTab === 'schedules' && (
                        <section className="portal-panel" style={{ marginBottom: '20px' }}>
                            <DataGridList
                                data={config.schedules || []}
                                keyExpr="scheduleName"
                                columns={scheduleColumns}
                                showPager={true}
                                pageSize={10}
                            />
                        </section>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="action-btn" style={{ background: 'var(--brand-mid)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }} onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
